import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Sparkles, Tag, ArrowUpRight } from 'lucide-react';

import nit from './ProgramImages/NIT_COURSE.jpeg';
import IDE from './ProgramImages/IDE_BOOTCAMP_IMG.webp';
import Defence from './ProgramImages/Defence.jpg';
import msmeImg from './ProgramImages/MSME.jpg';

// ─── Data ────────────────────────────────────────────────────────────────────
const latestPrograms = [
  {
    id: 'l4',
    title: 'MSME Design Scheme Awareness Program',
    subtitle: 'Design-Led Growth & Innovation',
    tag: 'MSME',
    description:
      "A dynamic program bringing together MSMEs, startups, and innovators to explore design-led growth. Featuring expert insights on design thinking, product value enhancement, and access to IIT Ropar's R&D and incubation ecosystem.",
    link: 'https://www.iitrpr.ac.in/',
    linkedinPost: 'https://www.linkedin.com/posts/iit-ropar-technology-business-incubator-foundation_msmedesignscheme-designinnovation-msmeawareness-activity-7428769784827142144-h4qc?utm_source=share&utm_medium=member_desktop&rcm=ACoAAE8tHmAB2fEDuspIXnObOi7VkENC3eZ6P3g',
    imageUrl: msmeImg,
    accent: '#2563eb',
  },
  // {
  //   id: 'l2',
  //   title: 'Innovation, Design & Entrepreneurship Bootcamp',
  //   subtitle: 'PM SHRI Schools',
  //   tag: 'Education',
  //   description:
  //     "An immersive IDE Bootcamp organized for PM SHRI School educators and students, bridging design thinking with entrepreneurial practice under the Skilling & Capacity Building initiative of IIT Ropar–TBIF.",
  //   link: 'https://www.iitrpr.ac.in/',
  //   linkedinPost: 'https://www.linkedin.com/posts/iit-ropar-technology-business-incubator-foundation_idebootcamp-inauguralceremony-innovationineducation-activity-7409250188269125632-6O7r?utm_source=share&utm_medium=member_desktop&rcm=ACoAAE8tHmAB2fEDuspIXnObOi7VkENC3eZ6P3g',
  //   imageUrl: IDE,
  //   accent: '#0891b2',
  // },
  {
    id: 'l1',
    title: 'Student Entrepreneurship & Innovation Engagement',
    subtitle: 'NIT Sikkim',
    tag: 'Academia',
    description:
      "A dynamic engagement program at NIT Sikkim fostering entrepreneurship and innovation mindset among students, with hands-on exposure to startup methodologies and IIT Ropar–TBIF's incubation ecosystem.",
    link: 'https://www.iitrpr.ac.in/',
    linkedinPost: 'https://www.linkedin.com/posts/iit-ropar-technology-business-incubator-foundation_iitropar-tbif-nitsikkim-activity-7407032541951537154-ae7L?utm_source=share&utm_medium=member_desktop&rcm=ACoAAE8tHmAB2fEDuspIXnObOi7VkENC3eZ6P3g',
    imageUrl: nit,
    accent: '#7c3aed',
  },
  {
    id: 'l3',
    title: 'Entrepreneurship & Industry Orientation',
    subtitle: 'Defence Personnel',
    tag: 'Defence',
    description:
      'A specialized orientation program tailored for defence personnel, focusing on industry transition, startup awareness, and entrepreneurial skills to empower veterans and active-duty members with civilian innovation pathways.',
    link: 'https://www.iitrpr.ac.in/',
    linkedinPost: 'https://www.linkedin.com/posts/placeholder-defence',
    imageUrl: Defence,
    accent: '#0f766e',
  },
];

// ─── Slider ───────────────────────────────────────────────────────────────────
function LatestSlider() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [dir, setDir] = useState('next');
  const timerRef = useRef(null);

  const total = latestPrograms.length;

  const go = (direction, target) => {
    if (animating) return;
    setDir(direction);
    setAnimating(true);
    const next =
      target !== undefined
        ? target
        : direction === 'next'
        ? (current + 1) % total
        : (current - 1 + total) % total;

    setTimeout(() => {
      setCurrent(next);
      setAnimating(false);
    }, 450);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => go('next'), 10000);
    return () => clearInterval(timerRef.current);
  });

  const prog = latestPrograms[current];

  return (
    <div className="mb-24">
      {/* Sub-header pill */}
      <div className="flex items-center gap-3 mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-bold uppercase tracking-widest border border-blue-100">
          <Sparkles size={13} /> Highlighted Initiatives
        </div>
        <div className="h-px flex-1 bg-gradient-to-r from-blue-100 to-transparent" />
      </div>

      {/* Card shell */}
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-white min-h-[460px] flex flex-col md:flex-row">

        {/* ── LEFT: Text panel ── */}
        <div className="flex-1 flex flex-col justify-between p-8 md:p-12 relative overflow-hidden">

          {/* Accent top-line */}
          <div
            className="absolute top-0 left-0 right-0 h-1 transition-colors duration-500"
            style={{ backgroundColor: prog.accent }}
          />

          {/* Content */}
          <div
            style={{
              transition: 'opacity 0.35s ease, transform 0.4s cubic-bezier(0.4,0,0.2,1)',
              opacity: animating ? 0 : 1,
              transform: animating
                ? dir === 'next' ? 'translateY(14px)' : 'translateY(-14px)'
                : 'translateY(0)',
            }}
          >
            {/* Tag */}
            <div className="mb-5">
              <span
                className="inline-block text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest border"
                style={{
                  color: prog.accent,
                  borderColor: prog.accent + '55',
                  backgroundColor: prog.accent + '12',
                }}
              >
                {prog.tag}
              </span>
            </div>

            {/* Title */}
            <h3 className="text-2xl md:text-[1.85rem] font-extrabold text-slate-900 leading-snug mb-3 max-w-lg">
              {prog.title}
            </h3>

            {/* Subtitle */}
            <p
              className="text-xs font-bold uppercase tracking-widest mb-5"
              style={{ color: prog.accent }}
            >
              {prog.subtitle}
            </p>

            {/* Divider */}
            <div
              className="w-10 h-0.5 mb-5 rounded"
              style={{ backgroundColor: prog.accent + '60' }}
            />

            {/* Description */}
            <p className="text-slate-500 text-[0.92rem] leading-relaxed max-w-md">
              {prog.description}
            </p>
          </div>

          {/* ── Bottom controls ── */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => go('prev')}
                className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-700 transition-all"
                aria-label="Previous"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex items-center gap-1.5">
                {latestPrograms.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => go(i > current ? 'next' : 'prev', i)}
                    aria-label={`Slide ${i + 1}`}
                    className="h-1.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === current ? 24 : 6,
                      backgroundColor: i === current ? prog.accent : '#cbd5e1',
                    }}
                  />
                ))}
              </div>

              <button
                onClick={() => go('next')}
                className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:border-slate-400 hover:text-slate-700 transition-all"
                aria-label="Next"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Counter */}
            <span className="text-xs font-bold text-slate-400 tabular-nums tracking-widest">
              {String(current + 1).padStart(2, '0')}&nbsp;/&nbsp;{String(total).padStart(2, '0')}
            </span>
          </div>
        </div>

        {/* ── RIGHT: Image panel ── */}
        <div className="md:w-[42%] relative bg-slate-100 overflow-hidden min-h-[260px]">
          <img
            key={current}
            src={prog.imageUrl}
            alt={prog.title}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              transition: 'opacity 0.5s ease',
              opacity: animating ? 0 : 1,
            }}
          />

          {/* Gradient for depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent pointer-events-none" />

          {/* LinkedIn arrow button — opens the program's LinkedIn post */}
          <div
            className="absolute bottom-4 right-4"
            style={{
              opacity: animating ? 0 : 1,
              transition: 'opacity 0.4s ease',
            }}
          >
            <a
              href={prog.linkedinPost}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm shadow-md flex items-center justify-center text-slate-700 hover:bg-blue-600 hover:text-white transition-all"
              aria-label="View LinkedIn post"
            >
              <ArrowUpRight size={15} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────
function Programs() {
  return (
    <section className="py-24 px-6 bg-white border-b border-slate-100" id="programs">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold uppercase tracking-widest mb-4 border border-slate-200">
            <Tag size={12} /> What we do
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
            Featured <span className="text-blue-600">Programs</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-lg">
            From incubation cohorts to specialized technical training, we provide the ecosystem for growth.
          </p>
        </div>

        <LatestSlider />

      </div>
    </section>
  );
}

export default Programs;